import React, { useState } from "react";
import { CheckCircle2, ArrowRight, ZoomIn } from "lucide-react";
import CaseStudyHeroVisual from "./CaseStudyHeroVisual";
import ImageLightbox from "./ImageLightbox";

// Shared cursive style used across the editorial accents.
const cursive = { fontFamily: "'Segoe Script', 'Caveat', cursive" };

// ---------------------------------------------------------------------------
// ProblemSection — "Where the pain was." / "The context."
// ---------------------------------------------------------------------------
export const ProblemSection = ({ cs }) => {
  if (!cs.problem || cs.problem.length === 0) return null;
  const isContext = cs.problemLabel === "Project Overview";
  return (
    <section className="max-w-[1200px] mx-auto px-6 lg:px-10 py-24 lg:py-28">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <div className="text-[12px] uppercase tracking-[0.18em] text-neutral-400 dark:text-neutral-500">
            {cs.problemLabel || "The problem"}
          </div>
          {isContext ? (
            <h2 className="mt-3 text-[28px] lg:text-[34px] leading-[1.15] tracking-[-0.025em] font-medium">
              The <span style={cursive} className="font-normal text-neutral-600 dark:text-neutral-400">context.</span>
            </h2>
          ) : (
            <h2 className="mt-3 text-[28px] lg:text-[34px] leading-[1.15] tracking-[-0.025em] font-medium">
              Where the <span style={cursive} className="font-normal text-neutral-600 dark:text-neutral-400">pain</span> was.
            </h2>
          )}
        </div>
        <ul className="lg:col-span-8 space-y-5">
          {cs.problem.map((p, i) => (
            <li key={p} className="flex gap-4 pb-5 border-b border-neutral-200 dark:border-neutral-800 last:border-b-0">
              <span className="shrink-0 mt-0.5 text-[12px] font-mono text-neutral-400 dark:text-neutral-500">
                0{i + 1}
              </span>
              <p className="text-[16px] lg:text-[17px] text-neutral-800 dark:text-neutral-200 leading-[1.65]">{p}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

// ---------------------------------------------------------------------------
// ProcessSection — "How I worked through it."
// ---------------------------------------------------------------------------
export const ProcessSection = ({ steps }) => (
  <section className="bg-neutral-50/60 dark:bg-neutral-900/40 border-y border-neutral-100 dark:border-neutral-800">
    <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-24 lg:py-28">
      <div className="max-w-[760px]">
        <div className="text-[12px] uppercase tracking-[0.18em] text-neutral-400 dark:text-neutral-500">Process</div>
        <h2 className="mt-3 text-[28px] lg:text-[40px] leading-[1.1] tracking-[-0.03em] font-medium">
          How I worked through <span style={cursive} className="font-normal text-neutral-600 dark:text-neutral-400">it.</span>
        </h2>
      </div>
      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
        {steps.map((p, i) => (
          <div
            key={p.title}
            className="group p-7 rounded-2xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 hover:border-neutral-300 dark:hover:border-neutral-700 transition-colors duration-300"
          >
            <div className="flex items-center gap-3">
              <span className="text-[12px] font-mono text-neutral-400 dark:text-neutral-500">0{i + 1}</span>
              <span className="h-px flex-1 bg-neutral-200 dark:bg-neutral-800" />
            </div>
            <h3 className="mt-5 text-[19px] lg:text-[20px] font-medium tracking-tight">{p.title}</h3>
            <p className="mt-3 text-[14.5px] text-neutral-600 dark:text-neutral-400 leading-[1.65]">{p.body}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ---------------------------------------------------------------------------
// SketchesSection — early-thinking notebook flows
// ---------------------------------------------------------------------------
export const SketchesSection = ({ sketches }) => {
  const [openIndex, setOpenIndex] = useState(null);
  return (
    <section className="bg-white dark:bg-neutral-950 border-y border-neutral-100 dark:border-neutral-800">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-24 lg:py-28">
        <div className="max-w-[760px]">
          <div className="text-[12px] uppercase tracking-[0.18em] text-neutral-400 dark:text-neutral-500">Early thinking</div>
          <h2 className="mt-3 text-[28px] lg:text-[40px] leading-[1.1] tracking-[-0.03em] font-medium">
            Flow <span style={cursive} className="font-normal text-neutral-600 dark:text-neutral-400">sketches.</span>
          </h2>
          <p className="mt-5 text-[16px] text-neutral-600 dark:text-neutral-400 leading-[1.65]">
            Before pixels, a notebook. The two flows that anchored every later screen — the
            high-level system flow on the left, and the user&apos;s decision branches on the right.
          </p>
        </div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-6">
          {sketches.map((s, i) => (
            <figure key={s.url || s.label} className="group flex flex-col">
              <button
                type="button"
                onClick={() => setOpenIndex(i)}
                aria-label={`Open ${s.label}`}
                className="relative aspect-square w-full rounded-2xl overflow-hidden bg-neutral-100 dark:bg-neutral-900 border border-neutral-200/70 dark:border-neutral-800/70 transition-transform duration-500 ease-out group-hover:-translate-y-1 cursor-zoom-in"
              >
                <img src={s.url} alt={s.label} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                <span className="pointer-events-none absolute inset-0 bg-neutral-950/0 group-hover:bg-neutral-950/15 transition-colors duration-300 flex items-end justify-end p-3">
                  <span className="opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all duration-300 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-white/95 text-neutral-900 text-[11.5px] font-medium shadow-md">
                    <ZoomIn className="w-3 h-3" strokeWidth={2.25} />
                    View
                  </span>
                </span>
              </button>
              <figcaption className="mt-4">
                <div className="flex items-baseline gap-2">
                  <span className="text-[11px] font-mono text-neutral-400 dark:text-neutral-500 tabular-nums">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-[14px] font-medium text-neutral-900 dark:text-neutral-50">{s.label}</span>
                </div>
                {s.caption && (
                  <p className="mt-1 text-[13px] text-neutral-500 dark:text-neutral-400 leading-[1.55]">{s.caption}</p>
                )}
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
      <ImageLightbox
        open={openIndex !== null}
        screens={sketches}
        index={openIndex}
        onClose={() => setOpenIndex(null)}
        onIndex={(n) => setOpenIndex(n)}
      />
    </section>
  );
};

// ---------------------------------------------------------------------------
// DesignGoalsSection
// ---------------------------------------------------------------------------
export const DesignGoalsSection = ({ goals }) => (
  <section className="bg-white dark:bg-neutral-950 border-t border-neutral-100 dark:border-neutral-800">
    <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-24 lg:py-28">
      <div className="max-w-[760px]">
        <div className="text-[12px] uppercase tracking-[0.18em] text-neutral-400 dark:text-neutral-500">Design goals</div>
        <h2 className="mt-3 text-[28px] lg:text-[40px] leading-[1.1] tracking-[-0.03em] font-medium">
          What we set out to <span style={cursive} className="font-normal text-neutral-600 dark:text-neutral-400">achieve.</span>
        </h2>
      </div>
      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
        {goals.map((g, i) => (
          <div
            key={g.title}
            className="p-7 rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-neutral-50/60 dark:bg-neutral-900/40"
          >
            <div className="flex items-center gap-3">
              <span className="text-[12px] font-mono text-neutral-400 dark:text-neutral-500 tabular-nums">0{i + 1}</span>
              <span className="h-px flex-1 bg-neutral-200 dark:bg-neutral-800" />
            </div>
            <h3 className="mt-5 text-[19px] lg:text-[20px] font-medium tracking-tight">{g.title}</h3>
            <p className="mt-3 text-[14.5px] text-neutral-600 dark:text-neutral-400 leading-[1.65]">{g.body}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ---------------------------------------------------------------------------
// MyApproachSection
// ---------------------------------------------------------------------------
export const MyApproachSection = ({ approach }) => (
  <section className="max-w-[1200px] mx-auto px-6 lg:px-10 py-24 lg:py-28">
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
      <div className="lg:col-span-4">
        <div className="text-[12px] uppercase tracking-[0.18em] text-neutral-400 dark:text-neutral-500">My approach</div>
        <h2 className="mt-3 text-[28px] lg:text-[34px] leading-[1.15] tracking-[-0.025em] font-medium">
          How I framed <span style={cursive} className="font-normal text-neutral-600 dark:text-neutral-400">the work.</span>
        </h2>
      </div>
      <div className="lg:col-span-8">
        <p className="text-[17px] lg:text-[19px] text-neutral-800 dark:text-neutral-200 leading-[1.55] max-w-[680px]">
          {approach.lead}
        </p>
        <div className="mt-8 text-[11px] uppercase tracking-[0.22em] text-neutral-400 dark:text-neutral-500">
          Core principles
        </div>
        <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {approach.principles.map((p, i) => (
            <li key={p} className="flex items-center gap-3 px-4 py-3 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900">
              <span className="text-[11px] font-mono text-neutral-400 dark:text-neutral-500 tabular-nums">0{i + 1}</span>
              <span className="text-[15px] text-neutral-800 dark:text-neutral-200">{p}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </section>
);

// ---------------------------------------------------------------------------
// DesignSystemSection
// ---------------------------------------------------------------------------
export const DesignSystemSection = ({ system }) => (
  <section className="bg-neutral-50/60 dark:bg-neutral-900/40 border-y border-neutral-100 dark:border-neutral-800">
    <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-24 lg:py-28">
      <div className="max-w-[760px]">
        <div className="text-[12px] uppercase tracking-[0.18em] text-neutral-400 dark:text-neutral-500">
          Design system thinking
        </div>
        <h2 className="mt-3 text-[28px] lg:text-[40px] leading-[1.1] tracking-[-0.03em] font-medium">
          Built to <span style={cursive} className="font-normal text-neutral-600 dark:text-neutral-400">scale.</span>
        </h2>
        <p className="mt-5 text-[16px] text-neutral-600 dark:text-neutral-400 leading-[1.65]">{system.lead}</p>
      </div>
      <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {system.items.map((item, i) => (
          <div
            key={item}
            className="px-4 py-5 rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 text-center"
          >
            <div className="text-[11px] font-mono text-neutral-400 dark:text-neutral-500 tabular-nums">0{i + 1}</div>
            <div className="mt-2 text-[14.5px] font-medium text-neutral-900 dark:text-neutral-50">{item}</div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ---------------------------------------------------------------------------
// PullQuoteSection
// ---------------------------------------------------------------------------
export const PullQuoteSection = ({ quote }) => (
  <section className="max-w-[1200px] mx-auto px-6 lg:px-10 py-20 lg:py-24">
    <blockquote
      style={cursive}
      className="text-[26px] sm:text-[34px] lg:text-[42px] leading-[1.2] tracking-[-0.02em] text-neutral-900 dark:text-neutral-50 max-w-[1000px]"
    >
      <span className="text-neutral-300 dark:text-neutral-700 mr-2">“</span>
      {quote}
      <span className="text-neutral-300 dark:text-neutral-700 ml-1">”</span>
    </blockquote>
  </section>
);

// ---------------------------------------------------------------------------
// FounderQuoteSection
// ---------------------------------------------------------------------------
const initialsOf = (name) =>
  (name || "")
    .trim()
    .split(/\s+/)
    .map((p) => p[0])
    .slice(0, 2)
    .join("");

export const FounderQuoteSection = ({ q }) => (
  <section className="max-w-[1200px] mx-auto px-6 lg:px-10 pb-20 lg:pb-24">
    <div className="rounded-3xl border border-neutral-200 dark:border-neutral-800 bg-gradient-to-br from-neutral-50 to-white dark:from-neutral-900 dark:to-neutral-950 p-10 lg:p-14">
      <div className="text-[12px] uppercase tracking-[0.18em] text-neutral-400 dark:text-neutral-500">
        {q.label || "What the founder said"}
      </div>
      <blockquote
        style={cursive}
        className="mt-5 text-[22px] sm:text-[28px] lg:text-[34px] leading-[1.3] tracking-[-0.015em] text-neutral-900 dark:text-neutral-50 max-w-[960px]"
      >
        <span className="text-neutral-300 dark:text-neutral-700 mr-1.5">“</span>
        {q.quote}
        <span className="text-neutral-300 dark:text-neutral-700 ml-1">”</span>
      </blockquote>
      <div className="mt-8 flex items-center gap-3">
        <div
          className="w-9 h-9 rounded-full bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 flex items-center justify-center text-[12.5px] font-medium tracking-wide"
          aria-hidden
        >
          {initialsOf(q.name)}
        </div>
        <div>
          <div className="text-[14.5px] font-medium text-neutral-900 dark:text-neutral-50 leading-tight">{q.name}</div>
          {q.role && (
            <div className="text-[12.5px] text-neutral-500 dark:text-neutral-400 leading-tight mt-0.5">{q.role}</div>
          )}
        </div>
      </div>
    </div>
  </section>
);

// ---------------------------------------------------------------------------
// OutcomeSection — wraps highlights and/or metrics with the lead heading.
// ---------------------------------------------------------------------------
const OutcomeHighlights = ({ items }) => (
  <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
    {items.map((h, i) => (
      <div
        key={h}
        className="p-6 rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 flex flex-col"
      >
        <div className="text-[11px] font-mono text-neutral-400 dark:text-neutral-500 tabular-nums">0{i + 1}</div>
        <div className="mt-4 text-[18px] lg:text-[19px] font-medium tracking-[-0.01em] text-neutral-900 dark:text-neutral-50 leading-[1.3]">
          {h}
        </div>
      </div>
    ))}
  </div>
);

const OutcomeMetrics = ({ items }) => (
  <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
    {items.map((m) => (
      <div
        key={m.label}
        className="p-6 rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900"
      >
        <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-400 dark:text-neutral-500">{m.label}</div>
        <div className="mt-4 flex items-baseline gap-2">
          <span className="text-[12.5px] text-neutral-400 dark:text-neutral-500 line-through">{m.before}</span>
          <ArrowRight className="w-3 h-3 text-neutral-400 dark:text-neutral-500" strokeWidth={2} />
        </div>
        <div className="mt-1 text-[26px] lg:text-[30px] font-medium tracking-[-0.02em] text-neutral-900 dark:text-neutral-50">
          {m.after}
        </div>
      </div>
    ))}
  </div>
);

export const OutcomeSection = ({ cs }) => {
  const hasHighlights = cs.outcomeHighlights && cs.outcomeHighlights.length > 0;
  const hasMetrics = cs.metrics && cs.metrics.length > 0;
  if (!cs.outcome && !hasHighlights && !hasMetrics) return null;
  return (
    <section className="max-w-[1200px] mx-auto px-6 lg:px-10 py-24 lg:py-28">
      <div className="max-w-[760px]">
        <div className="text-[12px] uppercase tracking-[0.18em] text-neutral-400 dark:text-neutral-500">Outcome</div>
        <h2 className="mt-3 text-[28px] lg:text-[40px] leading-[1.1] tracking-[-0.03em] font-medium">
          The <span style={cursive} className="font-normal text-neutral-600 dark:text-neutral-400">outcome.</span>
        </h2>
        {cs.outcome && (
          <p className="mt-5 text-[16px] text-neutral-600 dark:text-neutral-400 leading-[1.65]">{cs.outcome}</p>
        )}
      </div>
      {hasHighlights && <OutcomeHighlights items={cs.outcomeHighlights} />}
      {!hasHighlights && hasMetrics && <OutcomeMetrics items={cs.metrics} />}
    </section>
  );
};

// ---------------------------------------------------------------------------
// LearningsSection
// ---------------------------------------------------------------------------
export const LearningsSection = ({ learnings }) => {
  if (!learnings || learnings.length === 0) return null;
  return (
    <section className="max-w-[1200px] mx-auto px-6 lg:px-10 pb-24 lg:pb-28">
      <div className="rounded-3xl border border-neutral-200 dark:border-neutral-800 p-10 lg:p-14 bg-gradient-to-b from-white to-neutral-50/60 dark:from-neutral-900 dark:to-neutral-950">
        <div className="text-[12px] uppercase tracking-[0.18em] text-neutral-400 dark:text-neutral-500">Learnings</div>
        <h2 className="mt-3 text-[26px] lg:text-[32px] leading-[1.2] tracking-[-0.025em] font-medium max-w-[820px]">
          What I’d carry into the <span style={cursive} className="font-normal text-neutral-600 dark:text-neutral-400">next one.</span>
        </h2>
        <ul className="mt-8 space-y-4 max-w-[820px]">
          {learnings.map((l) => (
            <li key={l} className="flex gap-3">
              <CheckCircle2 className="w-4 h-4 mt-1 shrink-0 text-neutral-700 dark:text-neutral-300" strokeWidth={1.75} />
              <p className="text-[15.5px] text-neutral-800 dark:text-neutral-200 leading-[1.65]">{l}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

// ---------------------------------------------------------------------------
// ExtraScreensSection — renders an additional screens grid with its own title
// ---------------------------------------------------------------------------
export const ExtraScreensSection = ({ title, intro, screens, aspect, id, flowLabel, screensId }) => {
  const syntheticCs = {
    id: screensId || id,
    name: title || "More screens",
    screens,
    screenAspect: aspect,
  };
  return (
    <section className="border-t border-neutral-100 dark:border-neutral-800 bg-neutral-50/40 dark:bg-neutral-900/30">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-24 lg:py-28">
        {(title || intro) && (
          <div className="max-w-[760px] mb-12 lg:mb-14">
            <div className="text-[12px] uppercase tracking-[0.18em] text-neutral-400 dark:text-neutral-500">
              {flowLabel || "Another flow"}
            </div>
            {title && (
              <h2 className="mt-3 text-[28px] lg:text-[40px] leading-[1.1] tracking-[-0.03em] font-medium">{title}</h2>
            )}
            {intro && (
              <p className="mt-5 text-[16px] text-neutral-600 dark:text-neutral-400 leading-[1.65]">{intro}</p>
            )}
          </div>
        )}
        <CaseStudyHeroVisual cs={syntheticCs} />
      </div>
    </section>
  );
};

// ---------------------------------------------------------------------------
// ProjectOverviewSection — mission + personas
// ---------------------------------------------------------------------------
export const ProjectOverviewSection = ({ po }) => (
  <section className="max-w-[1200px] mx-auto px-6 lg:px-10 py-24 lg:py-28">
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
      <div className="lg:col-span-4">
        <div className="text-[12px] uppercase tracking-[0.18em] text-neutral-400 dark:text-neutral-500">Project Overview</div>
        <h2 className="mt-3 text-[28px] lg:text-[34px] leading-[1.15] tracking-[-0.025em] font-medium">
          The <span style={cursive} className="font-normal text-neutral-600 dark:text-neutral-400">mission.</span>
        </h2>
      </div>
      <div className="lg:col-span-8">
        {po.mission && (
          <p className="text-[16.5px] lg:text-[18px] text-neutral-800 dark:text-neutral-200 leading-[1.7] max-w-[760px]">
            {po.mission}
          </p>
        )}
        {po.personas && po.personas.length > 0 && (
          <div className="mt-12">
            <div className="text-[11px] uppercase tracking-[0.22em] text-neutral-400 dark:text-neutral-500">
              {po.personasHeading || "Primary users"}
            </div>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
              {po.personas.map((p, i) => (
                <div
                  key={p.title}
                  className="p-5 rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-neutral-50/60 dark:bg-neutral-900/40"
                >
                  <div className="text-[11px] font-mono text-neutral-400 dark:text-neutral-500 tabular-nums">
                    0{i + 1}
                  </div>
                  <h3 className="mt-3 text-[17px] lg:text-[18px] font-medium tracking-tight text-neutral-900 dark:text-neutral-50 leading-tight">
                    {p.title}
                  </h3>
                  <p className="mt-2 text-[14px] text-neutral-600 dark:text-neutral-400 leading-[1.6]">{p.body}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  </section>
);
